/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import {ModelResponse} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {UserType} from '../enums';
import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {deleteUser, getUser, updateUser} from '../util/requests';
import {ResponseData} from '@dvrd/fetch';

interface Props extends BaseProps {
    last_login?: string | null;
    email?: string;
    firstname?: string;
    lastname?: string;
    user_type?: UserType;
}

export interface UserFields extends BaseFields {
    last_login: string | null;
    email: string;
    firstname: string;
    lastname: string;
    user_type: UserType;
}

const fields: UserFields = {
    id: '',
    created_at: '',
    updated_at: '',
    last_login: null,
    email: '',
    firstname: '',
    lastname: '',
    user_type: UserType.USER
};

type FieldDefType = keyof UserFields;

const fieldsDefinition: Record<FieldDefType, FieldDefinition> = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, null, true),
    last_login: createFieldDef(ModelFieldType.STRING, null, true),
    email: createFieldDef(ModelFieldType.STRING),
    firstname: createFieldDef(ModelFieldType.STRING),
    lastname: createFieldDef(ModelFieldType.STRING),
    user_type: createFieldDef(ModelFieldType.STRING, UserType.USER)
};

const skipFields: Array<keyof UserFields> = [];

export default class User extends BaseModel<Props, UserFields, FieldDefType> {
    static get(callback: ModelResponse<User | null>): void {
        getUser({
            callback: (data: ResponseData) => {
                const success = data.success;
                const user = success ? new User(data.user) : null;
                callback(user, success, data);
            }
        });
    }

    constructor(props?: Props) {
        super(skipFields, fieldsDefinition, fields, props);
        this.build(props);
    }

    get lastLogin(): string | null {
        return this.fields.last_login;
    }

    set lastLogin(last_login: string | null) {
        this.fields.last_login = last_login;
    }

    get lastLoginMoment(): IDate | null {
        if (!this.fields.last_login?.length) return null;
        return new IDate(this.fields.last_login);
    }

    get email(): string {
        return this.fields.email;
    }

    set email(email: string) {
        this.fields.email = email;
    }

    get firstname(): string {
        return this.fields.firstname;
    }

    set firstname(firstname: string) {
        this.fields.firstname = firstname;
    }

    get lastname(): string {
        return this.fields.lastname;
    }

    set lastname(lastname: string) {
        this.fields.lastname = lastname;
    }

    get fullname(): string {
        let name = this.firstname;
        if (this.lastname.length) name += ` ${this.lastname}`;
        return name;
    }

    get userType(): UserType {
        return this.fields.user_type;
    }

    set userType(user_type: UserType) {
        this.fields.user_type = user_type;
    }

    get copy(): User {
        const fields: UserFields = {
            created_at: this.createdAt,
            email: this.email,
            firstname: this.firstname,
            id: this.id,
            last_login: this.lastLogin,
            lastname: this.lastname,
            user_type: this.userType,
            updated_at: this.updatedAt
        };
        return new User(fields);
    }

    update = (callback: ModelResponse<User>) => {
        updateUser({
            data: {}, callback: (data: ResponseData) => {
                const success = data.success;
                if (success) this.constructFields(data.user);
                if (callback) callback(this, success, data);
            }
        });
    };

    delete = (callback: ModelResponse<User>) => {
        deleteUser({
            callback: (data: ResponseData) => {
                if (callback) {
                    callback(this, data.success, data);
                }
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
        }
    };
}